import type { InputStatus } from 'antd/lib/_util/statusUtils';
import type { BaseType } from 'antd/lib/typography/Base';
import { type JSX, useCallback, useEffect, useMemo } from 'react';

import { SpecificationIntFloat } from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloat';
import type {
  IntFloatFieldValue,
  IntFloatValue,
  MeasureUnitIntFloatValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatField.types';
import { withField, withFieldError } from 'src/helpers';
import type { IWithFieldErrorProps } from 'src/helpers/withFieldError';
import type { IFieldProps } from 'src/types';
import type { IProductSpecificationFieldReference } from 'src/types/reference/IProductSpecificationReference';
import { SpecificationMeasureUnit } from 'src/types/reference/SpecificationMeasureUnit';

type SpecificationIntFloatFieldProps = IFieldProps &
  Omit<IWithFieldErrorProps, 'isShowErrorMessage'> & {
    specificationField: IProductSpecificationFieldReference;

    containerClassName?: string;
    className?: string;
    errorType: BaseType;
    status?: InputStatus;
    disabled?: boolean;
  };

export const SpecificationIntFloatField = withField(
  withFieldError(
    ({
      field: formikField,
      form: { setFieldValue, setFieldTouched },
      touchOnMount,

      specificationField,
      containerClassName,
      className,
      status,
      disabled,
    }: Readonly<SpecificationIntFloatFieldProps>): JSX.Element => {
      // biome-ignore lint/correctness/useExhaustiveDependencies: false
      const defaultValue: IntFloatValue = useMemo(() => {
        const fieldValue: IntFloatFieldValue = formikField.value as unknown as IntFloatFieldValue;

        if (typeof fieldValue === 'object' && fieldValue !== null && 'measureUnit' in fieldValue) {
          return fieldValue;
        }

        if (Array.isArray(fieldValue)) {
          if (typeof fieldValue[0] === 'number') {
            return (fieldValue as number[]).filter(Boolean).map((fieldValueItem) => ({
              value: fieldValueItem,
              measureUnit: SpecificationMeasureUnit.NONE,
            }));
          }

          return fieldValue.filter(Boolean) as MeasureUnitIntFloatValue[];
        }

        if (fieldValue != null) {
          return {
            value: fieldValue,
            measureUnit: SpecificationMeasureUnit.NONE,
          } as MeasureUnitIntFloatValue;
        }

        return specificationField.multiselect ? [] : null;

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const handleChange = useCallback(
        async (selectedValue: IntFloatValue) => {
          const formikValue: IntFloatFieldValue =
            selectedValue == null
              ? specificationField.multiselect
                ? []
                : null
              : Array.isArray(selectedValue)
                ? selectedValue.length > 0
                  ? selectedValue[0].measureUnit === SpecificationMeasureUnit.NONE
                    ? selectedValue.map((selectedValueItem) => selectedValueItem.value)
                    : selectedValue
                  : []
                : selectedValue.measureUnit === SpecificationMeasureUnit.NONE
                  ? selectedValue.value
                  : selectedValue;

          await setFieldValue(formikField.name, formikValue);
          await setFieldTouched(formikField.name, true);
        },
        [specificationField.multiselect, setFieldValue, formikField.name, setFieldTouched]
      );

      // biome-ignore lint/correctness/useExhaustiveDependencies: false
      useEffect(() => {
        if (touchOnMount) {
          void setFieldTouched(formikField.name, true);
        }
      }, []);

      return (
        <SpecificationIntFloat
          defaultValue={defaultValue}
          onChange={handleChange}
          field={specificationField}
          containerClassName={containerClassName}
          className={className}
          status={status}
          disabled={disabled}
        />
      );
    }
  )
);
